import { useEffect, useState, FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { isAxiosError } from "axios";
import api from "../../services/api";
import { Feature, PaginatedResponse, Tenant } from "../../api/types";

const PAGE_LIMIT = 12;

export function Tenants() {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTenant, setNewTenant] = useState({ name: "", description: "", cnpj: "" });
  const [editFields, setEditFields] = useState<Record<number, { description: string; cnpj: string }>>({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTenants, setTotalTenants] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const logError = (label: string, error: unknown) => {
    if (isAxiosError(error)) {
      console.warn(label, error.response?.status ?? "unknown", error.message);
      return;
    }
    if (error instanceof Error) {
      console.warn(label, error.message);
      return;
    }
    console.warn(label);
  };

  useEffect(() => {
    loadTenants(1, "");
    loadFeatures();
  }, []);

  async function loadTenants(pageToLoad = 1, searchValue = searchTerm) {
    try {
      const query = new URLSearchParams();
      query.set("page", String(pageToLoad));
      query.set("limit", String(PAGE_LIMIT));
      if (searchValue.trim()) {
        query.set("search", searchValue.trim());
      }
      const { data } = await api.get<PaginatedResponse<Tenant>>(`/tenants/all/paged?${query.toString()}`);
      setTenants(data.data);
      setPage(data.meta.page);
      setTotalPages(data.meta.totalPages);
      setTotalTenants(data.meta.total);
      const mapped: Record<number, { description: string; cnpj: string }> = {};
      data.data.forEach((t) => {
        mapped[t.id] = { description: t.description || "", cnpj: t.cnpj || "" };
      });
      setEditFields(mapped);
    } catch (error) {
      logError("Failed to load tenants", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadFeatures() {
    try {
      const { data } = await api.get<Feature[]>("/features");
      setFeatures(data);
    } catch (error) {
      logError("Failed to load features", error);
    }
  }

  async function handleCreateTenant(e: FormEvent) {
    e.preventDefault();
    try {
      const payload: any = { name: newTenant.name, description: newTenant.description };
      if (newTenant.cnpj.trim()) payload.cnpj = newTenant.cnpj.trim();
      await api.post("/tenants", payload);
      setShowCreateModal(false);
      setNewTenant({ name: "", description: "", cnpj: "" });
      loadTenants(page);
    } catch (error) {
      logError("Failed to create tenant", error);
      alert(t("admin.tenants.createError"));
    }
  }

  async function handleEnableFeature(tenantId: number, featureId: number) {
    try {
      await api.post(`/tenants/${tenantId}/features`, { feature_id: featureId });
      loadTenants(page); // Reload to update UI
    } catch (error) {
      logError("Failed to enable feature", error);
      alert(t("admin.tenants.enableFeatureError"));
    }
  }

  async function handleDisableFeature(tenantId: number, featureId: number) {
      if (!confirm(t("admin.tenants.disableFeatureConfirm"))) return;
      try {
        await api.delete(`/tenants/${tenantId}/features/${featureId}`);
        loadTenants(page); // Reload to update UI
      } catch (error) {
        logError("Failed to disable feature", error);
        alert(t("admin.tenants.disableFeatureError"));
      }
  }

  async function handleToggleStatus(tenant: Tenant) {
      const newStatus = tenant.status === 'active' ? 'inactive' : 'active';
      if (!confirm(t("admin.tenants.statusConfirm", { status: newStatus }))) return;
      try {
          await api.put(`/tenants/${tenant.id}/status`, { status: newStatus });
          loadTenants(page);
      } catch (error) {
          logError("Failed to update status", error);
          alert(t("admin.tenants.statusError"));
      }
  }

  async function handleDeleteTenant(id: number) {
      if (!confirm(t("admin.tenants.deleteConfirm"))) return;
      try {
          await api.delete(`/tenants/${id}`);
          loadTenants(page);
      } catch (error) {
          logError("Failed to delete tenant", error);
          alert(t("admin.tenants.deleteError"));
      }
  }

  async function handleUpdateTenant(id: number) {
    const current = editFields[id] || { description: "", cnpj: "" };
    const payload: any = { description: current.description };
    payload.cnpj = current.cnpj.trim() ? current.cnpj.trim() : null;
    try {
      await api.put(`/tenants/${id}`, payload);
      loadTenants(page);
      alert(t("admin.tenants.updateSuccess"));
    } catch (error) {
      logError("Failed to update tenant", error);
      alert(t("admin.tenants.updateError"));
    }
  }

  if (loading) return <p>{t("admin.tenants.loading")}</p>;

  return (
    <div className="shell">
      <div className="header">
        <div className="header-title">
          <h1>{t("admin.tenants.title")}</h1>
          <p className="muted">{t("admin.tenants.subtitle")}</p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const nextSearch = searchInput.trim();
              setSearchTerm(nextSearch);
              loadTenants(1, nextSearch);
            }}
            style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}
          >
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("admin.tenants.searchPlaceholder")}
              style={{ width: '260px' }}
            />
            <button className="secondary" type="submit">
              {t("admin.tenants.search")}
            </button>
            {searchTerm && (
              <button
                className="secondary"
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setSearchTerm("");
                  loadTenants(1, "");
                }}
              >
                {t("admin.tenants.clear")}
              </button>
            )}
          </form>
          <button onClick={() => setShowCreateModal(true)}>
            <span style={{ marginRight: '0.5rem' }}>+</span> {t("admin.tenants.newTenant")}
          </button>
        </div>
      </div>

      {showCreateModal && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}>
          <div className="card" style={{ width: '440px', padding: '2rem' }}>
            <h3 style={{ marginBottom: '1.5rem', fontSize: '1.25rem' }}>{t("admin.tenants.createTitle")}</h3>
            <form onSubmit={handleCreateTenant} className="stack">
              <label>
                {t("admin.tenants.name")}
                <input
                  value={newTenant.name}
                  placeholder={t("admin.tenants.namePlaceholder")}
                  onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                  required
                />
              </label>
              <label>
                {t("admin.tenants.description")}
                <input
                  value={newTenant.description}
                  placeholder={t("admin.tenants.descriptionPlaceholder")}
                  onChange={(e) => setNewTenant({ ...newTenant, description: e.target.value })}
                />
              </label>
              <label>
                {t("admin.tenants.cnpj")}
                <input
                  value={newTenant.cnpj}
                  onChange={(e) => setNewTenant({ ...newTenant, cnpj: e.target.value })}
                  placeholder={t("admin.tenants.cnpjPlaceholder")}
                />
              </label>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button type="submit" style={{ flex: 1 }}>{t("common.create")}</button>
                <button type="button" className="secondary" style={{ flex: 1 }} onClick={() => setShowCreateModal(false)}>
                  {t("common.cancel")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid">
        {tenants?.map((tenant) => (
          <div key={tenant.id} className="card" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: 0 }}>{tenant.name}</h3>
                <span style={{ 
                  display: 'inline-block',
                  marginTop: '0.5rem',
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '6px', 
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  backgroundColor: tenant.status === 'active' ? '#f0fdf4' : '#fef2f2',
                  color: tenant.status === 'active' ? '#16a34a' : '#ef4444',
                  border: `1px solid ${tenant.status === 'active' ? '#dcfce7' : '#fee2e2'}`
                }}>
                  {tenant.status === 'active' ? t("admin.tenants.statusActive") : t("admin.tenants.statusInactive")}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                    className="secondary small"
                    onClick={() => handleToggleStatus(tenant)}
                    style={{ padding: '0.4rem 0.6rem' }}
                    title={tenant.status === 'active' ? t("common.deactivate") : t("common.activate")}
                >
                  {tenant.status === 'active' ? '⏸' : '▶'}
                </button>
                <button 
                  className="secondary small" 
                  onClick={() => handleDeleteTenant(tenant.id)} 
                  style={{ padding: '0.4rem 0.6rem', color: '#ef4444', borderColor: '#fecaca' }}
                  title={t("common.delete")}
                >
                  🗑
                </button>
              </div>
            </div>
            
            <div style={{ fontSize: '0.875rem' }}>
              <p className="muted" style={{ marginBottom: '0.5rem' }}>
                {tenant.description || t("admin.tenants.noDescription")}
              </p>
              <p className="muted" style={{ fontSize: '0.75rem' }}>
                {t("admin.tenants.cnpjLabel")}: <strong>{tenant.cnpj || t("admin.tenants.notInformed")}</strong>
              </p>
              <p className="muted" style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                ID: <strong>{tenant.id}</strong>
              </p>
            </div>

            <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <details style={{ width: '100%' }}>
                <summary style={{ cursor: 'pointer', fontSize: '0.875rem', fontWeight: '600', color: 'var(--text-main)', listStyle: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{t("admin.tenants.editInfo")}</span>
                  <span style={{ fontSize: '0.75rem', opacity: 0.5 }}>▼</span>
                </summary>
                <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <label>
                    {t("admin.tenants.description")}
                    <input
                      value={editFields[tenant.id]?.description ?? ""}
                      onChange={(e) =>
                        setEditFields((prev) => ({
                          ...prev,
                          [tenant.id]: { ...(prev[tenant.id] || { description: "", cnpj: "" }), description: e.target.value },
                        }))
                      }
                    />
                  </label>
                  <label>
                    {t("admin.tenants.cnpj")}
                    <input
                      value={editFields[tenant.id]?.cnpj ?? ""}
                      placeholder={t("admin.tenants.cnpjPlaceholder")}
                      onChange={(e) =>
                        setEditFields((prev) => ({
                          ...prev,
                          [tenant.id]: { ...(prev[tenant.id] || { description: "", cnpj: "" }), cnpj: e.target.value },
                        }))
                      }
                    />
                  </label>
                  <button className="small" onClick={() => handleUpdateTenant(tenant.id)}>
                    {t("admin.tenants.update")}
                  </button>
                </div>
              </details>

              <div>
                <strong style={{ fontSize: '0.875rem', display: 'block', marginBottom: '0.75rem' }}>
                  {t("admin.tenants.featuresEnabled")}
                </strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    {tenant.features && tenant.features.map(f => (
                        <span key={f.id} style={{ 
                          backgroundColor: '#f1f5f9', 
                          border: '1px solid #e2e8f0',
                          padding: '0.25rem 0.5rem', 
                          borderRadius: '6px', 
                          fontSize: '0.75rem', 
                          fontWeight: '500',
                          display: 'flex', 
                          alignItems: 'center',
                          gap: '0.375rem'
                        }}>
                            {f.key}
                            <button 
                              onClick={() => handleDisableFeature(tenant.id, f.id)} 
                              style={{ padding: 0, background: 'none', color: '#94a3b8', fontSize: '1rem', lineHeight: 1 }}
                              title={t("common.remove")}
                            >×</button>
                        </span>
                    ))}
                    {(!tenant.features || tenant.features.length === 0) && (
                      <span style={{fontSize: '0.75rem', color: 'var(--text-muted)'}}>
                        {t("admin.tenants.noFeatures")}
                      </span>
                    )}
                </div>

                <select 
                    className="secondary"
                    onChange={(e) => {
                        if(e.target.value) handleEnableFeature(tenant.id, Number(e.target.value));
                    }}
                    value=""
                    style={{ width: '100%', fontSize: '0.75rem', height: '32px', padding: '0 0.5rem' }}
                >
                    <option value="">{t("admin.tenants.addFeature")}</option>
                    {features?.filter(f => !tenant.features?.some(tf => tf.id === f.id)).map(f => (
                        <option key={f.id} value={f.id}>{f.key}</option>
                    ))}
                </select>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem' }}>
        <span className="muted" style={{ fontSize: '0.85rem' }}>
          {t("admin.tenants.pageSummary", { page, totalPages, total: totalTenants })}
        </span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="secondary"
            disabled={page <= 1}
            onClick={() => loadTenants(page - 1, searchTerm)}
          >
            {t("common.previous")}
          </button>
          <button
            className="secondary"
            disabled={page >= totalPages}
            onClick={() => loadTenants(page + 1, searchTerm)}
          >
            {t("common.next")}
          </button>
        </div>
      </div>
    </div>
  );
}
